import {
  eachDayOfInterval,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
} from 'date-fns';
import { jsPDF } from 'jspdf';
import type { Shift } from '../types';
import { formatCompanyDate } from './companyDateFormat';

export interface InvoiceShiftSlot {
  startTime: string;
  endTime: string;
}

export interface InvoiceDayCell {
  shifts: InvoiceShiftSlot[];
}

export interface InvoiceEmployeeRow {
  id: string;
  employeeName: string;
  totalHours: number;
  days: InvoiceDayCell[];
}

export interface InvoiceReportData {
  areaName: string;
  companyName: string;
  monthLabel: string;
  periodStart: string;
  periodEnd: string;
  year: number;
  month: number;
  daysInMonth: number;
  dayHeaders: { weekday: string; day: number }[];
  employees: InvoiceEmployeeRow[];
  dailyTotals: number[];
  grandTotalHours: number;
}

const EMPLOYEES_PER_PAGE = 11;

/** PDF typography tuned to match the sample invoice report. */
const PDF_FONT = {
  headerArea: 12,
  headerTitle: 14,
  headerMeta: 11,
  tableHeader: 10,
  dayHeader: 9,
  employeeName: 10,
  hours: 10,
  shiftTime: 9,
  total: 10,
  pageFooter: 10,
} as const;

function newRowId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function formatTimeHhmm(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) return '';
    return format(d, 'HHmm');
  } catch {
    return '';
  }
}

export function getShiftPaidHours(shift: Shift): number {
  if (shift.paidHours != null && Number(shift.paidHours) >= 0) {
    return Number(shift.paidHours);
  }
  try {
    const start = parseISO(shift.startTime);
    const end = parseISO(shift.endTime);
    let minutes = (end.getTime() - start.getTime()) / (1000 * 60);
    if (minutes <= 0) minutes += 24 * 60;
    const unpaid = (shift.unpaidHours ?? 0) * 60;
    return Math.round(Math.max(0, (minutes - unpaid) / 60) * 100) / 100;
  } catch {
    return 0;
  }
}

function shiftDateKey(shift: Shift): string {
  try {
    return format(parseISO(shift.startTime), 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

function employeeDisplayName(shift: Shift): string {
  const emp = shift.employee;
  if (!emp) return `Employee #${shift.employeeId}`;
  return `${emp.firstName ?? ''} ${emp.lastName ?? ''}`.trim() || `Employee #${shift.employeeId}`;
}

function emptyDayCell(): InvoiceDayCell {
  return { shifts: [] };
}

function emptyDayCells(count: number): InvoiceDayCell[] {
  return Array.from({ length: count }, () => emptyDayCell());
}

/** Supports legacy snapshots with flat startTime/endTime on the day cell. */
export function normalizeDayCell(cell: unknown): InvoiceDayCell {
  if (!cell || typeof cell !== 'object') return emptyDayCell();
  const c = cell as Record<string, unknown>;
  if (Array.isArray(c.shifts)) {
    return {
      shifts: c.shifts.map((s) => {
        const slot = s as Record<string, unknown>;
        return {
          startTime: typeof slot.startTime === 'string' ? slot.startTime : '',
          endTime: typeof slot.endTime === 'string' ? slot.endTime : '',
        };
      }),
    };
  }
  const startTime = typeof c.startTime === 'string' ? c.startTime : '';
  const endTime = typeof c.endTime === 'string' ? c.endTime : '';
  if (startTime || endTime) {
    return { shifts: [{ startTime, endTime }] };
  }
  return emptyDayCell();
}

export function getDayShifts(cell: InvoiceDayCell): InvoiceShiftSlot[] {
  return (cell.shifts ?? []).filter((s) => s.startTime || s.endTime);
}

export function dayCellHasShifts(cell: InvoiceDayCell): boolean {
  return getDayShifts(cell).length > 0;
}

/** Split decimal hours into sample PDF lines: hours, ".MM", "hrs". */
export function formatInvoiceHoursThreeLine(hours: number): {
  hoursLine: string;
  minutesLine: string;
  unitLine: string;
} | null {
  if (hours <= 0) return null;
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return {
    hoursLine: String(h),
    minutesLine: `.${String(m).padStart(2, '0')}`,
    unitLine: 'hrs',
  };
}

export function buildDayHeaders(year: number, month: number): { weekday: string; day: number }[] {
  const start = startOfMonth(new Date(year, month - 1, 1));
  const end = endOfMonth(start);
  return eachDayOfInterval({ start, end }).map((d) => ({
    weekday: format(d, 'EEE'),
    day: d.getDate(),
  }));
}

export function recalculateInvoiceTotals(data: InvoiceReportData): InvoiceReportData {
  const dailyTotals = Array.from({ length: data.daysInMonth }, () => 0);
  const employees = data.employees.map((row) => {
    let rowTotal = 0;
    row.days.forEach((cell, dayIdx) => {
      for (const slot of getDayShifts(normalizeDayCell(cell))) {
        if (slot.startTime && slot.endTime) {
          const hours = computeHoursFromHhmm(slot.startTime, slot.endTime);
          rowTotal += hours;
          dailyTotals[dayIdx] = Math.round((dailyTotals[dayIdx] + hours) * 100) / 100;
        }
      }
    });
    return {
      ...row,
      days: row.days.map((d) => normalizeDayCell(d)),
      totalHours: Math.round(rowTotal * 100) / 100,
    };
  });

  const grandTotalHours = Math.round(
    employees.reduce((sum, row) => sum + row.totalHours, 0) * 100
  ) / 100;

  return {
    ...data,
    employees,
    dailyTotals: dailyTotals.map((h) => Math.round(h * 100) / 100),
    grandTotalHours,
  };
}

export function computeHoursFromHhmm(startHhmm: string, endHhmm: string): number {
  const parse = (v: string): number | null => {
    const s = v.replace(/[^0-9]/g, '');
    if (s.length < 3) return null;
    const padded = s.padStart(4, '0').slice(-4);
    const h = Number(padded.slice(0, 2));
    const m = Number(padded.slice(2, 4));
    if (h > 23 || m > 59) return null;
    return h * 60 + m;
  };
  const start = parse(startHhmm);
  const end = parse(endHhmm);
  if (start == null || end == null) return 0;
  let minutes = end - start;
  if (minutes <= 0) minutes += 24 * 60;
  return Math.round((minutes / 60) * 100) / 100;
}

export function buildInvoiceReportFromShifts(params: {
  shifts: Shift[];
  scheduleId: number;
  scheduleName: string;
  companyName: string;
  year: number;
  month: number;
}): InvoiceReportData {
  const { shifts, scheduleId, scheduleName, companyName, year, month } = params;
  const monthStart = startOfMonth(new Date(year, month - 1, 1));
  const monthEnd = endOfMonth(monthStart);
  const daysInMonth = monthEnd.getDate();
  const dayHeaders = buildDayHeaders(year, month);

  const monthStartKey = format(monthStart, 'yyyy-MM-dd');
  const monthEndKey = format(monthEnd, 'yyyy-MM-dd');

  const filtered = shifts.filter((s) => {
    if (s.scheduleId !== scheduleId) return false;
    const key = shiftDateKey(s);
    return key >= monthStartKey && key <= monthEndKey;
  });

  const byEmployee = new Map<number, { name: string; byDay: Map<number, InvoiceShiftSlot[]> }>();

  for (const shift of filtered) {
    const empId = shift.employeeId;
    const name = employeeDisplayName(shift);
    if (!byEmployee.has(empId)) {
      byEmployee.set(empId, { name, byDay: new Map() });
    }
    const entry = byEmployee.get(empId)!;
    if (name && name !== `Employee #${empId}`) entry.name = name;

    const dateKey = shiftDateKey(shift);
    const dayIndex = Number(dateKey.slice(8, 10)) - 1;
    if (dayIndex < 0 || dayIndex >= daysInMonth) continue;

    const startTime = formatTimeHhmm(shift.startTime);
    const endTime = formatTimeHhmm(shift.endTime);
    if (!startTime || !endTime) continue;

    const dayMap = entry.byDay;
    if (!dayMap.has(dayIndex)) dayMap.set(dayIndex, []);
    dayMap.get(dayIndex)!.push({ startTime, endTime });
  }

  const employees: InvoiceEmployeeRow[] = Array.from(byEmployee.entries())
    .map(([employeeId, { name, byDay }]) => {
      const days = emptyDayCells(daysInMonth);
      let totalHours = 0;

      byDay.forEach((slots, dayIdx) => {
        if (slots.length === 0) return;
        days[dayIdx] = { shifts: slots.map((s) => ({ ...s })) };
        for (const slot of slots) {
          totalHours += computeHoursFromHhmm(slot.startTime, slot.endTime);
        }
      });

      return {
        id: `emp-${employeeId}`,
        employeeName: name,
        totalHours: Math.round(totalHours * 100) / 100,
        days,
      };
    })
    .sort((a, b) => a.employeeName.localeCompare(b.employeeName, undefined, { sensitivity: 'base' }));

  const dailyTotals = Array.from({ length: daysInMonth }, () => 0);
  for (const row of employees) {
    row.days.forEach((cell, idx) => {
      for (const slot of getDayShifts(cell)) {
        if (slot.startTime && slot.endTime) {
          dailyTotals[idx] += computeHoursFromHhmm(slot.startTime, slot.endTime);
        }
      }
    });
  }

  const data: InvoiceReportData = {
    areaName: scheduleName,
    companyName,
    monthLabel: format(monthStart, 'MMMM yyyy'),
    periodStart: formatCompanyDate(monthStart),
    periodEnd: formatCompanyDate(monthEnd),
    year,
    month,
    daysInMonth,
    dayHeaders,
    employees,
    dailyTotals: dailyTotals.map((h) => Math.round(h * 100) / 100),
    grandTotalHours: Math.round(employees.reduce((s, e) => s + e.totalHours, 0) * 100) / 100,
  };

  return data;
}

export function createEmptyInvoiceReport(params: {
  scheduleName: string;
  companyName: string;
  year: number;
  month: number;
}): InvoiceReportData {
  const monthStart = startOfMonth(new Date(params.year, params.month - 1, 1));
  const monthEnd = endOfMonth(monthStart);
  const daysInMonth = monthEnd.getDate();
  return {
    areaName: params.scheduleName,
    companyName: params.companyName,
    monthLabel: format(monthStart, 'MMMM yyyy'),
    periodStart: formatCompanyDate(monthStart),
    periodEnd: formatCompanyDate(monthEnd),
    year: params.year,
    month: params.month,
    daysInMonth,
    dayHeaders: buildDayHeaders(params.year, params.month),
    employees: [],
    dailyTotals: Array.from({ length: daysInMonth }, () => 0),
    grandTotalHours: 0,
  };
}

export function addEmptyEmployeeRow(data: InvoiceReportData): InvoiceReportData {
  return recalculateInvoiceTotals({
    ...data,
    employees: [
      ...data.employees,
      {
        id: newRowId(),
        employeeName: 'New employee',
        totalHours: 0,
        days: emptyDayCells(data.daysInMonth),
      },
    ],
  });
}

function measureNameColumnWidth(
  doc: jsPDF,
  rows: InvoiceEmployeeRow[],
  includeTotal: boolean,
  grandTotalHours: number
): number {
  let maxW = 0;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(PDF_FONT.tableHeader);
  maxW = Math.max(maxW, doc.getTextWidth('Employee'));

  doc.setFont('helvetica', 'normal');
  for (const row of rows) {
    doc.setFontSize(PDF_FONT.employeeName);
    maxW = Math.max(maxW, doc.getTextWidth(row.employeeName));
    doc.setFontSize(PDF_FONT.hours);
    maxW = Math.max(maxW, doc.getTextWidth(`${row.totalHours.toFixed(2)} hrs`));
  }

  if (includeTotal) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(PDF_FONT.total);
    maxW = Math.max(maxW, doc.getTextWidth('Total'));
    maxW = Math.max(maxW, doc.getTextWidth(`${grandTotalHours.toFixed(2)} hrs`));
  }

  return Math.min(Math.max(maxW + 2.5, 24), 34);
}

const CELL_INSET = 0.55;
const BASE_ROW_H = 12.5;
const TOTAL_ROW_H = 13;

function truncateSingleLine(doc: jsPDF, text: string, maxW: number): string {
  if (doc.getTextWidth(text) <= maxW) return text;
  let s = text;
  while (s.length > 0 && doc.getTextWidth(`${s}…`) > maxW) {
    s = s.slice(0, -1);
  }
  return s.length < text.length ? `${s}…` : text;
}

function maxShiftsInRow(row: InvoiceEmployeeRow): number {
  let max = 1;
  for (const cell of row.days) {
    max = Math.max(max, getDayShifts(normalizeDayCell(cell)).length);
  }
  return max;
}

function rowHeightForEmployee(row: InvoiceEmployeeRow): number {
  const extraPairs = Math.max(0, maxShiftsInRow(row) - 1);
  return BASE_ROW_H + extraPairs * 3.8;
}

function drawShiftTimesInCell(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  shifts: InvoiceShiftSlot[]
): void {
  const slots = shifts.filter((s) => s.startTime || s.endTime);
  if (!slots.length) return;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(PDF_FONT.shiftTime);

  const textX = x + w / 2;
  let textY = y + CELL_INSET + 2.3;
  const lineStep = 2.75;
  const pairGap = 0.55;

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (slot.startTime) {
      doc.text(slot.startTime, textX, textY, { align: 'center' });
      textY += lineStep;
    }
    if (slot.endTime) {
      doc.text(slot.endTime, textX, textY, { align: 'center' });
      textY += lineStep;
    }
    if (i < slots.length - 1) textY += pairGap;
  }
}

function drawEmployeeNameCell(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  name: string,
  hoursLabel: string,
  bold = false
): void {
  doc.setFont('helvetica', bold ? 'bold' : 'normal');
  doc.setFontSize(bold ? PDF_FONT.total : PDF_FONT.employeeName);
  const singleName = truncateSingleLine(doc, name, w - 2.5);
  doc.text(singleName, x + 1.2, y + 4);
  doc.setFontSize(bold ? PDF_FONT.total : PDF_FONT.hours);
  doc.text(hoursLabel, x + 1.2, y + h - 2.1);
}

function drawTotalHoursThreeLine(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  hours: number,
  centered: boolean
): void {
  const parts = formatInvoiceHoursThreeLine(hours);
  if (!parts) return;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(PDF_FONT.shiftTime);
  const textX = centered ? x + w / 2 : x + 1.2;
  const align = centered ? ('center' as const) : ('left' as const);
  let textY = y + CELL_INSET + 2.5;
  const lineStep = 2.75;

  doc.text(parts.hoursLine, textX, textY, { align });
  textY += lineStep;
  doc.text(parts.minutesLine, textX, textY, { align });
  textY += lineStep;
  doc.text(parts.unitLine, textX, textY, { align });
}

function drawReportPage(
  doc: jsPDF,
  data: InvoiceReportData,
  employeeSlice: InvoiceEmployeeRow[],
  pageIndex: number,
  totalPages: number,
  includeTotalRow: boolean
): void {
  const pageW = doc.internal.pageSize.getWidth();
  const marginL = 8;
  const marginR = 8;
  const nameColW = measureNameColumnWidth(doc, employeeSlice, includeTotalRow, data.grandTotalHours);
  const dayColW = (pageW - marginL - marginR - nameColW) / data.daysInMonth;

  let y = 12;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(PDF_FONT.headerArea);
  doc.text(`Area : ${data.areaName}`, marginL, y);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(PDF_FONT.headerTitle);
  doc.text(`Monthly Schedule - ${data.monthLabel}`, marginL, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(PDF_FONT.headerMeta);
  doc.text(`Period - ${data.periodStart} To ${data.periodEnd}`, marginL, y);
  y += 5;
  doc.text(data.companyName, marginL, y);
  y += 7;

  const headerTop = y;
  const headerH = 13;

  doc.setDrawColor(180);
  doc.setLineWidth(0.15);

  let x = marginL;
  doc.rect(x, headerTop, nameColW, headerH);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(PDF_FONT.tableHeader);
  doc.text('Employee', x + 1.2, headerTop + 5);
  x += nameColW;

  doc.setFont('helvetica', 'normal');
  for (let d = 0; d < data.daysInMonth; d++) {
    doc.rect(x, headerTop, dayColW, headerH);
    const dh = data.dayHeaders[d];
    doc.setFontSize(PDF_FONT.dayHeader);
    doc.text(dh.weekday, x + dayColW / 2, headerTop + 3.5, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.text(String(dh.day), x + dayColW / 2, headerTop + 7.5, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.text('S', x + dayColW / 2, headerTop + 11, { align: 'center' });
    x += dayColW;
  }

  y = headerTop + headerH;

  for (const row of employeeSlice) {
    const rowH = rowHeightForEmployee(row);
    x = marginL;
    doc.rect(x, y, nameColW, rowH);
    drawEmployeeNameCell(doc, x, y, nameColW, rowH, row.employeeName, `${row.totalHours.toFixed(2)} hrs`);
    x += nameColW;

    for (let d = 0; d < data.daysInMonth; d++) {
      doc.rect(x, y, dayColW, rowH);
      const cell = normalizeDayCell(row.days[d]);
      const shifts = getDayShifts(cell);
      if (shifts.length > 0) {
        drawShiftTimesInCell(doc, x, y, dayColW, rowH, shifts);
      }
      x += dayColW;
    }
    y += rowH;
  }

  if (includeTotalRow) {
    const rowH = TOTAL_ROW_H;
    x = marginL;
    doc.rect(x, y, nameColW, rowH);
    drawEmployeeNameCell(
      doc,
      x,
      y,
      nameColW,
      rowH,
      'Total',
      `${data.grandTotalHours.toFixed(2)} hrs`,
      true
    );
    x += nameColW;

    for (let d = 0; d < data.daysInMonth; d++) {
      doc.rect(x, y, dayColW, rowH);
      drawTotalHoursThreeLine(doc, x, y, dayColW, rowH, data.dailyTotals[d], true);
      x += dayColW;
    }
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(PDF_FONT.pageFooter);
  doc.text(`Page ${pageIndex + 1} of ${totalPages}`, pageW - marginR, doc.internal.pageSize.getHeight() - 6, {
    align: 'right',
  });
}

export function createInvoiceReportPdfBlob(data: InvoiceReportData): Blob {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const employees = data.employees;
  const chunks: InvoiceEmployeeRow[][] = [];

  if (employees.length === 0) {
    chunks.push([]);
  } else {
    for (let i = 0; i < employees.length; i += EMPLOYEES_PER_PAGE) {
      chunks.push(employees.slice(i, i + EMPLOYEES_PER_PAGE));
    }
  }

  const totalPages = chunks.length;
  chunks.forEach((slice, idx) => {
    if (idx > 0) doc.addPage();
    drawReportPage(doc, data, slice, idx, totalPages, idx === totalPages - 1);
  });

  return doc.output('blob');
}

export function monthYearFromInput(value: string): { year: number; month: number } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return { year, month };
}

export function defaultMonthInputValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function normalizeHhmmInput(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '').slice(0, 4);
  return digits;
}

/** Parse snapshot JSON from server into InvoiceReportData (returns null if invalid). */
export function parseInvoiceReportSnapshot(raw: unknown): InvoiceReportData | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.areaName !== 'string' || !Array.isArray(o.employees) || typeof o.daysInMonth !== 'number') {
    return null;
  }
  const data = o as unknown as InvoiceReportData;
  return recalculateInvoiceTotals({
    ...data,
    employees: data.employees.map((row) => ({
      ...row,
      days: row.days.map((d) => normalizeDayCell(d)),
    })),
  });
}
